<template>
    <ExportWrapper :file-name="fileName">
        <ExportConditionStatusTitleSection />
        
        <div v-if="!!componentStatusData?.isNoActionSet" class="section-wrapper">
            <ConditionStatusHeaderNoActionCategoryButton :is-no-action-set="!!componentStatusData?.isNoActionSet" is-export-layout />
        </div>

        <div class="master-data-wrapper">
            <MasterDataPanel :generator-id="generatorId" :show-more-default="masterDataShowMore" is-export-layout />
        </div>

        <div class="table-wrapper">
            <ConditionStatusLatestReportsSection is-export-layout :component-id="generatorId" :component-type="ComponentType.Generator" />
        </div>

        <div class="section-wrapper">
            <ExportConditionStatusRecommendationsSection v-if="userPermissions.canViewGeneratorRecommendations" :component-id="generatorId" :preload-size="recommendationsPreloadSize" />
        </div>
        
        <div class="section-wrapper">
            <ConditionStatusMeasurementsSection is-export-layout />
        </div>

        <div class="section-wrapper">
            <ExportConditionStatusVisualInspectionSection />
        </div>

        <div class="section-wrapper">
            <ExportConditionStatusAnalysisCommentsSection :component-id="generatorId" :component-type="ComponentType.Generator" :preload-size="analysisCommentsPreloadSize" />
        </div>
        
        <div class="section-wrapper">
            <ExportConditionStatusGeneralCommentsSection :generator-id="generatorId" />
        </div>
        
        <div class="section-wrapper">
            <ExportConditionStatusHistoricalTrendsSection :generator-id="generatorId" />
        </div>
        
        <ExportFooterSection />
    </ExportWrapper>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import ExportWrapper from '@/pages/exports/ExportWrapper.vue'
import ExportConditionStatusTitleSection from '@/pages/exports/generators/condition-status/sections/ExportConditionStatusTitleSection.vue'
import ConditionStatusHeaderNoActionCategoryButton from '@/pages/transformers/condition-status/sections/no-action-category/ConditionStatusHeaderNoActionCategoryButton.vue'
import MasterDataPanel from '@/pages/generators/condition-status/sections/master-data/MasterDataPanel.vue'
import ConditionStatusLatestReportsSection from '@/pages/common/component-status/ConditionStatusLatestReportsSection.vue'
import ExportConditionStatusRecommendationsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusRecommendationsSection.vue'
import ConditionStatusMeasurementsSection from '@/pages/generators/condition-status/sections/measurements/ConditionStatusMeasurementsSection.vue'
import ExportConditionStatusAnalysisCommentsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusAnalysisCommentsSection.vue'
import ExportConditionStatusGeneralCommentsSection from '@/pages/exports/generators/condition-status/sections/ExportConditionStatusGeneralCommentsSection.vue'
import ExportConditionStatusHistoricalTrendsSection from '@/pages/exports/generators/condition-status/sections/ExportConditionStatusHistoricalTrendsSection.vue'
import ExportConditionStatusVisualInspectionSection from '@/pages/exports/generators/condition-status/sections/ExportConditionStatusVisualInspectionSection.vue'
import ExportFooterSection from '@/pages/exports/common/ExportFooterSection.vue'
import { DATE_FORMAT } from '@/utils/date-utils'
import { format } from 'date-fns'
import { provideGeneratorId } from '@/composables/useGeneratorId'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import { ComponentType } from '@/configuration/component/component-types'
import { useRoute } from 'vue-router'
import { usePermissions } from '@/composables/usePermissions'

const props = defineProps<{
    generatorId: string
}>()

const { generatorId } = toRefs(props)

const { query } = useRoute()
const { userPermissions } = usePermissions()

const { data: generatorDetailsData } = useGeneratorDetailsQuery(generatorId)
const { data: componentStatusData } = useComponentStatusQuery(generatorId.value, ComponentType.Generator)

const fileName = computed(() => {
    if (!generatorDetailsData.value) {
        return undefined
    }

    const plantGroupName = generatorDetailsData.value.riverGroupName
    const plantName = generatorDetailsData.value.powerPlantName
    const generatorSid = generatorDetailsData.value.generatorID
    const currentDate = format(new Date(), DATE_FORMAT)

    return `HCA_ComponentStatus_${plantGroupName}_${plantName}_${generatorSid}_${currentDate}`
})

const masterDataShowMore = computed(() => {
    const value = query.masterDataShowMore as string

    return value === 'true' ? true : false
})

const recommendationsPreloadSize = computed(() => {
    const size = parseInt(query.recommendationsLength as string)

    return isNaN(size) ? undefined : size
})

const analysisCommentsPreloadSize = computed(() => {
    const size = parseInt(query.analysisCommentsLength as string)

    return isNaN(size) ? undefined : size
})

provideGeneratorId(generatorId)
</script>

<style scoped lang="scss">
:deep(.content-panel) {
    border: 1px solid $pbl-divider;
    box-shadow: unset;
}

.section-wrapper {
    padding: $spacing-xs $spacing-s;
}

.master-data-wrapper {
    padding: $spacing-xs $spacing-s;
}

.table-wrapper {
    break-inside: avoid;
    padding: $spacing-xs $spacing-s;

    :deep(.badge) {
        font-size: $default-font-size;
        padding: $spacing-2xs $spacing-s;
    }

    :deep(.analysis-table) {
        margin-top: 8px;
    }
}

.chart-wrapper {
    break-inside: avoid;
    padding: $spacing-xs $spacing-s;
}
</style>
